import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { set_notification_variable } from '../../../redux/action_creators/home_actions';
import { get_api, put_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';

const BASE_POLL_MS = 60 * 1000;
const JITTER_MIN_MS = 4000;
const JITTER_MAX_MS = 8000;

const nextPollDelay = () =>
  BASE_POLL_MS + Math.floor(JITTER_MIN_MS + Math.random() * (JITTER_MAX_MS - JITTER_MIN_MS));

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

function AvailabilityToggle() {
  const dispatch = useDispatch();
  const [available, setAvailable] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchAvailability = useCallback(async () => {
    try {
      const resp = await get_api(getUrl('/v2/twilio_call/availability'), true);
      const next = !!resp.data?.data?.available;
      setAvailable(next);
      window.dispatchEvent(
        new CustomEvent('twilio-agent-availability-changed', { detail: { available: next } })
      );
    } catch (e) {
      dispatch(set_notification_variable(true, 'error', 'Failed to fetch agent availability'));
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAvailability();
    const timers = [];
    const run = async () => {
      await fetchAvailability();
      timers.push(setTimeout(run, nextPollDelay()));
    };
    timers.push(setTimeout(run, nextPollDelay()));
    return () => timers.forEach(clearTimeout);
  }, [fetchAvailability]);

  useEffect(() => {
    const onChange = (e) => setAvailable(!!e?.detail?.available);
    window.addEventListener('twilio-agent-availability-changed', onChange);
    return () => window.removeEventListener('twilio-agent-availability-changed', onChange);
  }, []);

  const toggleAvailability = async () => {
    if (toggling) return;
    const next = !available;
    setToggling(true);
    setAvailable(next);
    window.dispatchEvent(
      new CustomEvent('twilio-agent-availability-changed', { detail: { available: next } })
    );
    try {
      await put_api(getUrl('/v2/twilio_call/availability'), { available: next }, true);
    } catch (e) {
      dispatch(set_notification_variable(true, 'error', 'Failed to update availability'));
      setAvailable(!next);
      window.dispatchEvent(
        new CustomEvent('twilio-agent-availability-changed', { detail: { available: !next } })
      );
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2.5">
      <div className="inline-flex flex-col items-end leading-tight whitespace-nowrap">
        <span className="text-[11px] font-medium text-white">Call status</span>
        <span className={`text-xs font-bold ${available ? 'text-green-500' : 'text-gray-500'}`}>
          {available ? 'Available' : 'Unavailable'}
        </span>
      </div>
      <button
        type="button"
        onClick={toggleAvailability}
        disabled={toggling}
        role="switch"
        aria-checked={available}
        aria-label="Toggle availability"
        className={`relative inline-flex h-7 w-[60px] shrink-0 items-center rounded-full p-[3px] border-0 transition-colors duration-300 ease-in-out focus:outline-none ${
          available ? 'bg-orange-500' : 'bg-gray-300'
        } ${toggling ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
      >
        <span
          aria-hidden="true"
          className={`absolute left-2 text-[10px] font-bold uppercase tracking-wider text-white transition-opacity duration-200 ${
            available ? 'opacity-100' : 'opacity-0'
          }`}
        >
          On
        </span>
        <span
          aria-hidden="true"
          className={`absolute right-2 text-[10px] font-bold uppercase tracking-wider text-gray-600 transition-opacity duration-200 ${
            available ? 'opacity-0' : 'opacity-100'
          }`}
        >
          Off
        </span>
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-block h-[22px] w-[22px] rounded-full bg-white shadow-sm transform transition-transform duration-300 ease-in-out ${
            available ? 'translate-x-[32px]' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

export default AvailabilityToggle;
